import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PlanTab from './settings/PlanTab';
import { buttonClass } from './settings/SettingsUI';
import { LogOutIcon } from './icons/LucideIcons';

/**
 * What the owner of a suspended shop sees after signing in (billing B3): a
 * billing-only session (`user.billingOnly`) reaches nothing but Plan &
 * billing, so the app shows only that, with "Reactivate" (a new checkout) or,
 * for an unpaid bill, "Pay the bill" and "Update card", and a short
 * explanation that matches (QA B P1-4: it used to say "Reactivate below"
 * over a past-due shop that had no such button). The rest of the app's
 * screens, providers and banners are never mounted (their calls would be
 * refused).
 *
 * On arrival it refreshes the session once: a shop reactivated since the
 * sign-in (a card updated in the portal, a webhook that just landed) opens
 * the full app straight away. Returning from Checkout, PlanTab keeps
 * refreshing until the webhook has reactivated the shop.
 */
export function BillingOnlyGate({ children }) {
  const { user } = useAuth();
  if (user && user.billingOnly) return <BillingOnlyScreen />;
  return <>{children}</>;
}

/** The explainer for what Plan & billing offers this shop. */
export function billingOnlyExplainer(data) {
  const kept = 'Your orders, customers and settings are all kept.';
  const owes = data && (data.pastDue || (data.subscription && data.subscription.status === 'past_due'));
  if (owes) {
    return `Your shop is closed because a payment didn't go through, so the rest of the app is unavailable. ${kept} `
      + 'Pay the bill or update your card below, and everything is back as soon as the payment goes through.';
  }
  return `Your shop is closed for now, so the rest of the app is unavailable. ${kept} `
    + 'Choose a plan below to reactivate, and everything is back as soon as the payment goes through.';
}

export default function BillingOnlyScreen() {
  const { logout, refreshSession } = useAuth();
  const [data, setData] = useState(null);
  const onLoaded = useCallback((d) => setData(d), []);

  useEffect(() => {
    if (refreshSession) refreshSession().catch(() => {});
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-surface-alt px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-[1240px] flex-col gap-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 max-w-2xl">
            <h1 className="font-heading text-[28px] leading-tight text-gray-900">Plan &amp; billing</h1>
            <p className="mt-1 text-sm text-gray-700" data-testid="billing-only-explainer">
              {data ? billingOnlyExplainer(data) : 'Your shop is closed for now, so the rest of the app is unavailable. Your orders, customers and settings are all kept.'}
            </p>
          </div>
          <button type="button" onClick={logout} className={buttonClass.secondary}>
            <LogOutIcon size={16} />
            Sign out
          </button>
        </header>
        <PlanTab billingOnly onLoaded={onLoaded} />
      </div>
    </div>
  );
}
